/**
 * Color conversion utilities for theming.
 * Converts hex colors to HSL format for CSS custom properties.
 */

import { hslToRgb } from "./hsl-to-rgb";
import { rgbToHex } from "./rgb-to-hex";

export interface HSL {
  h: number; // 0-360
  s: number; // 0-100
  l: number; // 0-100
}

/**
 * Validates a hex color string.
 * Accepts 3-digit (#RGB) or 6-digit (#RRGGBB) formats, with or without the # prefix.
 *
 * @param hex - The hex color string to validate
 * @returns true if valid hex color format
 *
 * @example
 * ```typescript
 * isValidHexColor("#ff0000");  // true
 * isValidHexColor("ff0000");   // true
 * isValidHexColor("#f00");     // true
 * isValidHexColor("invalid");  // false
 * ```
 */
export function isValidHexColor(hex: string): boolean {
  if (!hex || typeof hex !== "string") return false;
  const cleanHex = hex.startsWith("#") ? hex.slice(1) : hex;
  return /^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/.test(cleanHex);
}

/**
 * Converts a hex color string to HSL values.
 *
 * @param hex - The hex color string (e.g., "#ff0000" or "ff0000")
 * @returns HSL object with h (0-360), s (0-100), l (0-100), or null if invalid
 *
 * @example
 * ```typescript
 * hexToHsl("#ff0000");  // { h: 0, s: 100, l: 50 } (red)
 * hexToHsl("#1e40af");  // { h: 224, s: 73, l: 40 } (blue)
 * hexToHsl("invalid");  // null
 * ```
 */
export function hexToHsl(hex: string): HSL | null {
  if (!isValidHexColor(hex)) return null;

  let cleanHex = hex.startsWith("#") ? hex.slice(1) : hex;

  // Expand 3-digit hex to 6-digit
  if (cleanHex.length === 3) {
    cleanHex = cleanHex
      .split("")
      .map((c) => c + c)
      .join("");
  }

  // Parse RGB values
  const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  if (max === min) {
    // Achromatic (gray)
    return { h: 0, s: 0, l: Math.round(l * 100) };
  }

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let h: number;
  switch (max) {
    case r:
      h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
      break;
    case g:
      h = ((b - r) / d + 2) / 6;
      break;
    case b:
    default:
      h = ((r - g) / d + 4) / 6;
      break;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

/**
 * Converts HSL values to CSS custom property format.
 * Returns the format expected by Tailwind/shadcn: "H S% L%" (space-separated, no hsl() wrapper).
 *
 * @param hsl - The HSL object
 * @returns CSS-ready HSL string (e.g., "224 76% 40%")
 *
 * @example
 * ```typescript
 * hslToCssValue({ h: 224, s: 76, l: 40 });  // "224 76% 40%"
 * hslToCssValue({ h: 0, s: 0, l: 100 });    // "0 0% 100%"
 * ```
 */
export function hslToCssValue(hsl: HSL): string {
  return `${hsl.h} ${hsl.s}% ${hsl.l}%`;
}

/**
 * Converts a hex color directly to CSS custom property format.
 * Combines hexToHsl and hslToCssValue for convenience.
 *
 * @param hex - The hex color string
 * @returns CSS-ready HSL string, or null if invalid hex
 *
 * @example
 * ```typescript
 * hexToCssValue("#1e40af");  // "224 73% 40%"
 * hexToCssValue("invalid");  // null
 * ```
 */
export function hexToCssValue(hex: string): string | null {
  const hsl = hexToHsl(hex);
  if (!hsl) return null;
  return hslToCssValue(hsl);
}

/**
 * Calculates the relative luminance of an HSL color.
 * Used for determining appropriate foreground colors for accessibility.
 *
 * @param hsl - The HSL object
 * @returns Relative luminance value (0-1)
 */
function getRelativeLuminance(hsl: HSL): number {
  // For HSL, lightness is a good proxy for perceived brightness
  // but we can be more accurate by converting back to RGB
  const l = hsl.l / 100;
  const s = hsl.s / 100;
  const h = hsl.h;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;

  let r = 0,
    g = 0,
    b = 0;

  if (h >= 0 && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (h >= 60 && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (h >= 120 && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (h >= 180 && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (h >= 240 && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else {
    r = c;
    g = 0;
    b = x;
  }

  r = r + m;
  g = g + m;
  b = b + m;

  // Calculate relative luminance per WCAG formula
  const toLinear = (val: number) => (val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4));

  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

/**
 * Determines the optimal foreground color (light or dark) for a given background color.
 * Uses WCAG contrast ratio guidelines to ensure accessibility.
 *
 * @param backgroundHsl - The background color as HSL
 * @returns HSL for either near-white or near-black foreground
 *
 * @example
 * ```typescript
 * // Dark background -> light foreground
 * getContrastingForeground({ h: 224, s: 76, l: 20 });  // { h: 0, s: 0, l: 98 }
 *
 * // Light background -> dark foreground
 * getContrastingForeground({ h: 224, s: 76, l: 80 });  // { h: 0, s: 0, l: 9 }
 * ```
 */
export function getContrastingForeground(backgroundHsl: HSL): HSL {
  const luminance = getRelativeLuminance(backgroundHsl);

  // Use the same values as globals.css defaults
  // Light foreground: 0 0% 98% (near white)
  // Dark foreground: 0 0% 9% (near black)
  // Threshold chosen for WCAG AA compliance (4.5:1 contrast ratio)
  return luminance > 0.179 ? { h: 0, s: 0, l: 9 } : { h: 0, s: 0, l: 98 };
}

/**
 * Generates both background and foreground CSS values from a hex color.
 * Useful for applying a theme color with automatic foreground contrast.
 *
 * @param hex - The hex color string for the background
 * @returns Object with cssValue for background and foregroundCssValue, or null if invalid
 *
 * @example
 * ```typescript
 * generateThemeColors("#1e40af");
 * // { cssValue: "224 73% 40%", foregroundCssValue: "0 0% 98%" }
 * ```
 */
export function generateThemeColors(hex: string): { cssValue: string; foregroundCssValue: string } | null {
  const hsl = hexToHsl(hex);
  if (!hsl) return null;

  const foregroundHsl = getContrastingForeground(hsl);

  return {
    cssValue: hslToCssValue(hsl),
    foregroundCssValue: hslToCssValue(foregroundHsl),
  };
}

/**
 * Returns an HSL CSS value for a color, darkened only when needed so it stays readable as text
 * or a border on a light surface. Caps lightness so a pale color (e.g. a light yellow primary)
 * becomes a darker shade of itself rather than low-contrast; darker colors pass through unchanged.
 *
 * @param hex - The hex color string
 * @returns CSS-ready HSL string with lightness capped at 40%, or null if invalid hex
 *
 * @example
 * ```typescript
 * getReadableAccentHsl("#f3e9d2");  // "35 47% 40%" (darkened)
 * getReadableAccentHsl("#1e40af");  // "224 73% 40%" (unchanged, already <= 40% lightness)
 * ```
 */
export function getReadableAccentHsl(hex: string): string | null {
  const hsl = hexToHsl(hex);
  if (!hsl) return null;
  return hslToCssValue({ ...hsl, l: Math.min(hsl.l, 40) });
}

/**
 * Calculates a contrasting foreground color (light or dark) for a given background hex color.
 * Returns the result as a hex color string.
 *
 * @param backgroundHex - The background color in hex format (e.g., "#1e40af")
 * @returns A contrasting foreground color in hex format, or null if invalid input
 *
 * @example
 * ```typescript
 * // Dark background -> light foreground
 * getContrastingForegroundHex("#1e40af");  // "#fafafa"
 *
 * // Light background -> dark foreground
 * getContrastingForegroundHex("#f0f0f0");  // "#171717"
 * ```
 */
export const getContrastingForegroundHex = (backgroundHex: string): string | null => {
  const hsl = hexToHsl(backgroundHex);
  if (!hsl) return null;

  const foregroundHsl = getContrastingForeground(hsl);
  const [r, g, b] = hslToRgb(foregroundHsl.h, foregroundHsl.s / 100, foregroundHsl.l / 100);
  return rgbToHex(r, g, b);
};
